/**
 * Operations Hub figures (Ops dashboard v1, docs/specs/portal/operations-hub.md): statistics across every project
 * of a tenant for a date range, then per project and per aircraft. Every figure is built on an existing rule:
 *
 * - km flown per day: `flownCreditM` of the line statuses of the day's flights (decisions 32, 176, 197);
 * - accepted km per day: `acceptedM` (reviewed, `use_km`; ADR-013), counted on the day the line was flown;
 * - flights, survey days, fuel used: the Job Summary's daily-entry rules (`jobSummary`: flight entries that are not
 *   mobilisation; survey days are dates with survey hours);
 * - flight hours: `hoursFlown` (survey + comp box + cals + ferry, decision 162);
 * - accepted km per survey hour: `acceptedKmPerHour` (TJR "Acptd Line KM/Hr");
 * - standby, maintenance and suspended days: Σ `days_alloc` by entry type, standby by TJR's four standby types;
 * - progress: `projectProgress` (the Progress page and the Job Summary); contract km: `areaLineKm` (decision 169)
 *   plus the km of the variations the client agreed (decision 258);
 * - utilisation: an aircraft's available days run from its first to its last daily entry on the project (decision
 *   228), clipped to the range; "flew on N of M available days" and flight hours per available day.
 *
 * The cloud functions (`ops_line_status`, `ops_daily_entries`, `20261020010000_operations_oversight.sql`) only select
 * the rows; nothing here is repeated in SQL.
 */
import { isoWeekOf, isoWeekMonday } from './dates.js';
import { hoursFlown } from './aircraft.js';
import { blockKmCompleted } from './block-report.js';
import type { Acceptance, Block, DailyEntry, IsoDate, LineStatus, PlannedLine, Uuid } from './model.js';
import { projectProgress } from './portal.js';
import { flownCreditM } from './progress.js';
import { areaLineKm } from './quote-calc.js';
import type { QuoteArea } from './quote-model.js';
import { acceptedM } from './tjr-adapter.js';
import { acceptedKmPerHour, estPercentComplete } from './tjr-summary.js';

/**
 * Roles that open the Operations Hub (display only; `ops_can_read` in the database decides). Office staff read it
 * too (decision 231); the page has no actions, so every role reads it only.
 */
export const OPS_ROLES: readonly string[] = ['tenant_admin', 'manager', 'office_staff', 'platform_admin'];

export function canOps(roles: readonly string[]): boolean {
  return roles.some((r) => OPS_ROLES.includes(r));
}

/** One line status as `ops_line_status` returns it: the columns the km rules need, with its flight's day and aircraft. */
export interface OpsLine {
  readonly project_id: Uuid;
  readonly flight_id: Uuid;
  /** The local date the line was flown (`flight.date`). */
  readonly flight_date: IsoDate;
  readonly aircraft_id: Uuid | null;
  readonly rego: string | null;
  readonly line_number: number;
  readonly acceptance: Acceptance;
  readonly use_km: boolean;
  readonly flown_m: number;
  readonly proposed_m: number;
  readonly accepted_m: number;
  readonly log_completion: string | null;
}

/** One daily entry as `ops_daily_entries` returns it. */
export type OpsEntry = Pick<
  DailyEntry,
  | 'id'
  | 'project_id'
  | 'date'
  | 'aircraft_id'
  | 'entry_type'
  | 'standby_type'
  | 'days_alloc'
  | 'svy_hours'
  | 'cbx_hours'
  | 'cal_hours'
  | 'fry_hours'
  | 'ext_hours'
  | 'fuel_used_l'
  | 'mobilization'
  | 'flt_num'
  | 'op_comment'
>;

/** The Teslyn entities' line status and flight as Ops rows (for tests and for Field later). */
export function opsLines(
  statuses: readonly Pick<
    LineStatus,
    | 'project_id'
    | 'flight_id'
    | 'line_number'
    | 'acceptance'
    | 'use_km'
    | 'flown_m'
    | 'proposed_m'
    | 'accepted_m'
    | 'log_completion'
    | 'deleted_at'
  >[],
  flights: readonly {
    readonly id: Uuid;
    readonly date: IsoDate;
    readonly aircraft_id: Uuid | null;
    readonly rego: string | null;
    readonly deleted_at: string | null;
  }[],
): OpsLine[] {
  const byId = new Map(flights.filter((f) => f.deleted_at === null).map((f) => [f.id, f]));
  const out: OpsLine[] = [];
  for (const s of statuses) {
    const f = byId.get(s.flight_id);
    if (s.deleted_at !== null || !f) continue;
    out.push({
      project_id: s.project_id,
      flight_id: s.flight_id,
      flight_date: f.date,
      aircraft_id: f.aircraft_id,
      rego: f.rego,
      line_number: s.line_number,
      acceptance: s.acceptance,
      use_km: s.use_km,
      flown_m: s.flown_m,
      proposed_m: s.proposed_m,
      accepted_m: s.accepted_m,
      log_completion: s.log_completion ?? null,
    });
  }
  return out;
}

// ---------------------------------------------------------------- range and filters

export interface OpsRange {
  /** Inclusive local dates, `YYYY-MM-DD`. */
  readonly from: IsoDate;
  readonly to: IsoDate;
}

export interface OpsFilter extends OpsRange {
  /** One project, or null for every project of the tenant. */
  readonly projectId: Uuid | null;
  /** One aircraft, or null for every aircraft. Entries without an aircraft leave when one is chosen. */
  readonly aircraftId: Uuid | null;
}

export type RangePreset = 'last7' | 'last30' | 'this_week' | 'last_week';

export const RANGE_PRESET_LABEL: Readonly<Record<RangePreset | 'custom', string>> = {
  last7: 'Last 7 days',
  last30: 'Last 30 days',
  this_week: 'This week',
  last_week: 'Last week',
  custom: 'Custom',
};

/** The default range (spec §4, Q7): the last 30 days. */
export const DEFAULT_RANGE_PRESET: RangePreset = 'last30';

/** Ranges are limited to a year (the km-per-day chart draws every day). */
export const OPS_MAX_RANGE_DAYS = 366;

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const toDate = (d: IsoDate): Date => new Date(`${d}T00:00:00Z`);
const iso = (d: Date): IsoDate => d.toISOString().slice(0, 10);
const addDays = (d: Date, n: number): Date => new Date(d.getTime() + n * 86_400_000);

/** A preset's dates for `today` (the viewer's local date). Weeks are ISO weeks, Monday to Sunday, as the weekly report. */
export function presetRange(preset: RangePreset, today: IsoDate): OpsRange {
  const t = toDate(today);
  switch (preset) {
    case 'last7':
      return { from: iso(addDays(t, -6)), to: today };
    case 'last30':
      return { from: iso(addDays(t, -29)), to: today };
    case 'this_week':
    case 'last_week': {
      const w = isoWeekOf(t);
      const monday = isoWeekMonday(w.isoYear, w.week);
      const start = preset === 'this_week' ? monday : addDays(monday, -7);
      return { from: iso(start), to: iso(addDays(start, 6)) };
    }
  }
}

/** Why a range cannot be shown, in words; null when it can. */
export function rangeIssue(r: OpsRange): string | null {
  if (!ISO_DATE.test(r.from) || Number.isNaN(toDate(r.from).getTime()))
    return 'Enter the first day of the range.';
  if (!ISO_DATE.test(r.to) || Number.isNaN(toDate(r.to).getTime())) return 'Enter the last day of the range.';
  if (r.from > r.to) return 'The first day must be on or before the last day.';
  if (rangeLength(r) > OPS_MAX_RANGE_DAYS) return `Choose a range of at most ${OPS_MAX_RANGE_DAYS} days.`;
  return null;
}

/** Days in an inclusive range. */
export function rangeLength(r: OpsRange): number {
  return Math.round((toDate(r.to).getTime() - toDate(r.from).getTime()) / 86_400_000) + 1;
}

/** Every date of an inclusive range, oldest first. */
export function rangeDays(r: OpsRange): IsoDate[] {
  const out: IsoDate[] = [];
  for (let d = toDate(r.from); iso(d) <= r.to; d = addDays(d, 1)) out.push(iso(d));
  return out;
}

const inRange = (d: IsoDate, r: OpsRange): boolean => d >= r.from && d <= r.to;

export function filterLines(lines: readonly OpsLine[], f: OpsFilter): OpsLine[] {
  return lines.filter(
    (l) =>
      inRange(l.flight_date, f) &&
      (f.projectId === null || l.project_id === f.projectId) &&
      (f.aircraftId === null || l.aircraft_id === f.aircraftId),
  );
}

export function filterEntries<E extends OpsEntry>(entries: readonly E[], f: OpsFilter): E[] {
  return entries.filter(
    (e) =>
      inRange(e.date, f) &&
      (f.projectId === null || e.project_id === f.projectId) &&
      (f.aircraftId === null || e.aircraft_id === f.aircraftId),
  );
}

// ---------------------------------------------------------------- km per day

export interface KmDay {
  readonly date: IsoDate;
  /** Km flown on the decision 32 basis (`flownCreditM`), pending lines included. */
  readonly flownKm: number;
  /** Accepted km of the reviewed, `use_km` statuses (`acceptedM`). */
  readonly acceptedKm: number;
  /** Lines still awaiting review. */
  readonly pendingLines: number;
  /** Flights with at least one line that day. */
  readonly flights: number;
}

const flownKmOf = (lines: readonly OpsLine[]): number =>
  lines.reduce((a, l) => a + flownCreditM(l), 0) / 1000;
const acceptedKmOf = (lines: readonly OpsLine[]): number => acceptedM(lines) / 1000;

/** Km flown and accepted on every day of the range (days with nothing flown included, at 0). */
export function kmPerDay(lines: readonly OpsLine[], f: OpsFilter): KmDay[] {
  const mine = filterLines(lines, f);
  const byDay = new Map<IsoDate, OpsLine[]>();
  for (const l of mine) byDay.set(l.flight_date, [...(byDay.get(l.flight_date) ?? []), l]);
  return rangeDays(f).map((date) => {
    const day = byDay.get(date) ?? [];
    return {
      date,
      flownKm: flownKmOf(day),
      acceptedKm: acceptedKmOf(day),
      pendingLines: day.filter((l) => l.acceptance === 'pending').length,
      flights: new Set(day.map((l) => l.flight_id)).size,
    };
  });
}

// ---------------------------------------------------------------- aircraft and projects

/** How a row names its aircraft: the aircraft id, else the flight's registration, else none recorded. */
export function opsAircraftKey(aircraftId: Uuid | null, rego?: string | null): string {
  if (aircraftId) return aircraftId;
  return rego ? `rego:${rego}` : 'none';
}

/** The Job Summary's flight entries: flight type, not mobilisation (ferry-only days count, TJR "Flights"). */
const isFlightEntry = (e: OpsEntry): boolean => e.entry_type === 'flight' && !e.mobilization;

export interface UseFigures {
  /** TJR "Flights": flight entries that are not mobilisation. */
  readonly flights: number;
  /** Survey + comp box + cals + ferry hours (`hoursFlown`). */
  readonly flightHours: number;
  readonly surveyHours: number;
  /** Distinct dates with survey hours on a flight entry. */
  readonly surveyDays: number;
  readonly flownKm: number;
  readonly acceptedKm: number;
  /** Accepted km ÷ survey hours; null without survey hours. */
  readonly acceptedKmPerSurveyHour: number | null;
  /** Fuel used on flight entries that are not mobilisation (TJR Summary "Fuel Used"), litres. */
  readonly fuelUsedL: number;
}

function useFigures(lines: readonly OpsLine[], entries: readonly OpsEntry[]): UseFigures {
  const flightEntries = entries.filter(isFlightEntry);
  const surveyHours = flightEntries.reduce((a, e) => a + e.svy_hours, 0);
  const acceptedKm = acceptedKmOf(lines);
  return {
    flights: flightEntries.length,
    flightHours: entries.reduce((a, e) => a + hoursFlown(e), 0),
    surveyHours,
    surveyDays: new Set(flightEntries.filter((e) => e.svy_hours > 0).map((e) => e.date)).size,
    flownKm: flownKmOf(lines),
    acceptedKm,
    acceptedKmPerSurveyHour: surveyHours > 0 ? acceptedKmPerHour(acceptedKm, surveyHours) : null,
    fuelUsedL: flightEntries.reduce((a, e) => a + e.fuel_used_l, 0),
  };
}

/**
 * An aircraft's first and last live daily entry on a project, over the whole project (`ops_aircraft_spans`). Entries
 * without an aircraft (TJR's standby days) belong to the project only (decision 230) and are no aircraft's entry.
 */
export interface AircraftSpan {
  readonly project_id: Uuid;
  readonly aircraft_id: Uuid;
  readonly first_date: IsoDate;
  readonly last_date: IsoDate;
}

/** The spans of the given entries: what `ops_aircraft_spans` returns when given every entry of the project. */
export function aircraftSpans(
  entries: readonly Pick<OpsEntry, 'project_id' | 'aircraft_id' | 'date'>[],
): AircraftSpan[] {
  const by = new Map<
    string,
    { project_id: Uuid; aircraft_id: Uuid; first_date: IsoDate; last_date: IsoDate }
  >();
  for (const e of entries) {
    if (e.aircraft_id === null) continue;
    const k = `${e.project_id}|${e.aircraft_id}`;
    const s = by.get(k);
    if (!s)
      by.set(k, {
        project_id: e.project_id,
        aircraft_id: e.aircraft_id,
        first_date: e.date,
        last_date: e.date,
      });
    else {
      if (e.date < s.first_date) s.first_date = e.date;
      if (e.date > s.last_date) s.last_date = e.date;
    }
  }
  return [...by.values()].sort(
    (a, b) => a.project_id.localeCompare(b.project_id) || a.aircraft_id.localeCompare(b.aircraft_id),
  );
}

export interface Utilisation {
  /** Dates of the available days with flight hours (survey, comp box, cals or ferry) on the aircraft's entries. */
  readonly flyingDays: number;
  /** Days from the aircraft's first to its last entry on the project, inside the range (decision 228). */
  readonly availableDays: number;
  /**
   * Project days (MB's R52): every calendar day the aircraft is on the job inside the range, flown or not (standby,
   * maintenance, travel or an empty day between entries). The same span as `availableDays`, under the name the
   * Operations Hub shows. On the table's total row (decision 548) it is calendar days: the days any of the rows'
   * aircraft is on the job, each counted once (two aircraft for the same 9 days give 9).
   */
  readonly projectDays: number;
  /** The first and last project day inside the range; null with none. The total row's union is built from these. */
  readonly projectSpan: { readonly from: IsoDate; readonly to: IsoDate } | null;
  /**
   * Flight hours ÷ project days ("Average flight hours per day on the job"); null with no project days. The name is
   * kept from decision 228.
   */
  readonly flightHoursPerAvailableDay: number | null;
}

/** The available days of a span inside a range; 0 when they do not meet. */
function availableIn(span: AircraftSpan | undefined, r: OpsRange): { from: IsoDate; to: IsoDate } | null {
  if (!span) return null;
  const from = span.first_date > r.from ? span.first_date : r.from;
  const to = span.last_date < r.to ? span.last_date : r.to;
  return from <= to ? { from, to } : null;
}

function utilisationOf(
  span: AircraftSpan | undefined,
  entries: readonly OpsEntry[],
  flightHours: number,
  r: OpsRange,
): Utilisation {
  const days = availableIn(span, r);
  if (!days)
    return {
      flyingDays: 0,
      availableDays: 0,
      projectDays: 0,
      projectSpan: null,
      flightHoursPerAvailableDay: null,
    };
  const availableDays = rangeLength(days);
  const flyingDays = new Set(
    entries.filter((e) => inRange(e.date, days) && hoursFlown(e) > 0).map((e) => e.date),
  ).size;
  return {
    flyingDays,
    availableDays,
    projectDays: availableDays,
    projectSpan: days,
    flightHoursPerAvailableDay: flightHours / availableDays,
  };
}

/** Calendar days covered by any of the spans, each day once. */
function calendarDays(spans: readonly ({ readonly from: IsoDate; readonly to: IsoDate } | null)[]): number {
  const sorted = spans
    .filter((x): x is { readonly from: IsoDate; readonly to: IsoDate } => x !== null)
    .sort((a, b) => a.from.localeCompare(b.from));
  let total = 0;
  let cur: { from: IsoDate; to: IsoDate } | null = null;
  for (const x of sorted) {
    if (cur && x.from <= cur.to) {
      if (x.to > cur.to) cur.to = x.to;
      continue;
    }
    if (cur) total += rangeLength(cur);
    cur = { from: x.from, to: x.to };
  }
  return cur ? total + rangeLength(cur) : total;
}

/**
 * The table's total: aircraft-days flown and on the job, flight hours per aircraft-day on the job, and the project
 * days as calendar days (decision 548: any aircraft on the job that day, each day once).
 */
export function utilisationTotal(
  rows: readonly (Utilisation & Pick<UseFigures, 'flightHours'>)[],
): Utilisation {
  const flyingDays = rows.reduce((a, r) => a + r.flyingDays, 0);
  const availableDays = rows.reduce((a, r) => a + r.availableDays, 0);
  const hours = rows.reduce((a, r) => a + (r.availableDays > 0 ? r.flightHours : 0), 0);
  return {
    flyingDays,
    availableDays,
    projectDays: calendarDays(rows.map((r) => r.projectSpan)),
    projectSpan: (() => {
      const spans = rows.map((r) => r.projectSpan).filter((x) => x !== null);
      if (spans.length === 0) return null;
      return {
        from: spans.reduce((a, x) => (x.from < a ? x.from : a), spans[0]!.from),
        to: spans.reduce((a, x) => (x.to > a ? x.to : a), spans[0]!.to),
      };
    })(),
    flightHoursPerAvailableDay: availableDays > 0 ? hours / availableDays : null,
  };
}

export interface AircraftProjectRow extends UseFigures, Utilisation {
  readonly projectId: Uuid;
  /** `opsAircraftKey`: the aircraft id, `rego:<registration>` or `none`. */
  readonly aircraftKey: string;
  readonly aircraftId: Uuid | null;
  /** The registration a flight recorded when the aircraft is not linked. */
  readonly rego: string | null;
}

/**
 * One row per project and aircraft that flew, recorded hours or was on the project in the filter: flights, hours,
 * survey days, km, km per survey hour, fuel and utilisation. Entries with no aircraft (TJR standby days) belong to the
 * project only (decision 230) and are left out. `spans` are the aircraft's first and last entries over the whole
 * project (`ops_aircraft_spans`); without them the filter's own entries give the spans.
 */
export function aircraftProjectRows(
  lines: readonly OpsLine[],
  entries: readonly OpsEntry[],
  f: OpsFilter,
  spans?: readonly AircraftSpan[],
): AircraftProjectRow[] {
  const ls = filterLines(lines, f);
  const es = filterEntries(entries, f).filter((e) => e.aircraft_id !== null);
  const spanList = (spans ?? aircraftSpans(es)).filter(
    (s) =>
      (f.projectId === null || s.project_id === f.projectId) &&
      (f.aircraftId === null || s.aircraft_id === f.aircraftId),
  );
  const spanOf = new Map(spanList.map((s) => [`${s.project_id}|${s.aircraft_id}`, s]));
  const keys = new Map<
    string,
    { projectId: Uuid; key: string; aircraftId: Uuid | null; rego: string | null }
  >();
  const add = (projectId: Uuid, aircraftId: Uuid | null, rego: string | null): string => {
    const key = `${projectId}|${opsAircraftKey(aircraftId, rego)}`;
    if (!keys.has(key)) keys.set(key, { projectId, key: opsAircraftKey(aircraftId, rego), aircraftId, rego });
    return key;
  };
  const linesOf = new Map<string, OpsLine[]>();
  for (const l of ls) {
    const k = add(l.project_id, l.aircraft_id, l.aircraft_id ? null : l.rego);
    linesOf.set(k, [...(linesOf.get(k) ?? []), l]);
  }
  const entriesOf = new Map<string, OpsEntry[]>();
  for (const e of es) {
    if (e.entry_type !== 'flight') continue;
    const k = add(e.project_id, e.aircraft_id, null);
    entriesOf.set(k, [...(entriesOf.get(k) ?? []), e]);
  }
  // an aircraft on the project in the range with no flight (standby or maintenance with its registration) still has
  // available days
  for (const s of spanList) if (availableIn(s, f)) add(s.project_id, s.aircraft_id, null);
  return [...keys.entries()]
    .map(([k, v]) => {
      const mine = entriesOf.get(k) ?? [];
      const figures = useFigures(linesOf.get(k) ?? [], mine);
      return {
        projectId: v.projectId,
        aircraftKey: v.key,
        aircraftId: v.aircraftId,
        rego: v.rego,
        ...figures,
        ...utilisationOf(v.aircraftId ? spanOf.get(k) : undefined, mine, figures.flightHours, f),
      };
    })
    .sort((a, b) => a.projectId.localeCompare(b.projectId) || a.aircraftKey.localeCompare(b.aircraftKey));
}

// ---------------------------------------------------------------- standby, maintenance, suspended

export type StandbyKind = 'diurnal' | 'weather' | 'pilot_rdo' | 'other' | 'unspecified';

/** TJR's standby types (`sbTypeCB`), in its order. */
export const STANDBY_LABEL: Readonly<Record<StandbyKind, string>> = {
  diurnal: 'Diurnal',
  weather: 'Weather',
  pilot_rdo: 'Pilot RDO',
  other: 'Other',
  unspecified: 'Type not recorded',
};

export type DayKind = 'standby' | 'maintenance' | 'suspended';

export const DAY_KIND_LABEL: Readonly<Record<DayKind, string>> = {
  standby: 'Standby',
  maintenance: 'Maintenance',
  suspended: 'Suspended',
};

export interface DayItem {
  readonly id: Uuid;
  readonly date: IsoDate;
  readonly projectId: Uuid;
  readonly aircraftId: Uuid | null;
  readonly kind: DayKind;
  /** Standby only. */
  readonly standbyType: StandbyKind | null;
  readonly days: number;
  /** The entry's operational comment: the crew's reason. */
  readonly reason: string;
}

export interface StandbyDays {
  /** TJR "Days Standby": Σ days of standby entries. */
  readonly total: number;
  readonly byType: Readonly<Record<StandbyKind, number>>;
  /** Entries of each standby type (TJR "Diurnal/Weather Events" counts these). */
  readonly events: Readonly<Record<StandbyKind, number>>;
}

export interface DaysSummary {
  readonly standby: StandbyDays;
  readonly maintenance: number;
  readonly suspended: number;
  /** Every standby, maintenance and suspended entry of the filter, by date. */
  readonly items: readonly DayItem[];
  /** With an aircraft chosen: the entries of the range and project left out because they record no aircraft. */
  readonly withoutAircraft: number;
}

const zeroByType = (): Record<StandbyKind, number> => ({
  diurnal: 0,
  weather: 0,
  pilot_rdo: 0,
  other: 0,
  unspecified: 0,
});

export function daysSummary(entries: readonly OpsEntry[], f: OpsFilter): DaysSummary {
  const isDay = (e: OpsEntry): boolean => e.entry_type !== 'flight';
  const items: DayItem[] = filterEntries(entries, f)
    .filter(isDay)
    .map((e) => ({
      id: e.id,
      date: e.date,
      projectId: e.project_id,
      aircraftId: e.aircraft_id,
      kind: e.entry_type as DayKind,
      standbyType: e.entry_type === 'standby' ? ((e.standby_type ?? 'unspecified') as StandbyKind) : null,
      days: e.days_alloc,
      reason: e.op_comment.trim(),
    }))
    .sort((a, b) => a.date.localeCompare(b.date) || a.projectId.localeCompare(b.projectId));
  const byType = zeroByType();
  const events = zeroByType();
  for (const i of items) {
    if (i.kind !== 'standby' || i.standbyType === null) continue;
    byType[i.standbyType] += i.days;
    events[i.standbyType] += 1;
  }
  const sumOf = (k: DayKind): number => items.filter((i) => i.kind === k).reduce((a, i) => a + i.days, 0);
  const withoutAircraft =
    f.aircraftId === null
      ? 0
      : filterEntries(entries, { ...f, aircraftId: null }).filter((e) => isDay(e) && e.aircraft_id === null)
          .length;
  return {
    standby: { total: sumOf('standby'), byType, events },
    maintenance: sumOf('maintenance'),
    suspended: sumOf('suspended'),
    items,
    withoutAircraft,
  };
}

// ---------------------------------------------------------------- summary tiles

export interface OpsSummary extends UseFigures {
  readonly standby: StandbyDays;
  readonly maintenanceDays: number;
  readonly suspendedDays: number;
}

/** The tiles: every figure over the whole filter. Survey days are calendar days with survey hours on any aircraft. */
export function opsSummary(
  lines: readonly OpsLine[],
  entries: readonly OpsEntry[],
  f: OpsFilter,
): OpsSummary {
  const days = daysSummary(entries, f);
  return {
    ...useFigures(filterLines(lines, f), filterEntries(entries, f)),
    standby: days.standby,
    maintenanceDays: days.maintenance,
    suspendedDays: days.suspended,
  };
}

// ---------------------------------------------------------------- progress against the plan and the contract

export type ContractArea = Pick<QuoteArea, 'traverse_km' | 'tie_km' | 'line_km_override' | 'lines_file'>;

/** Line km of a contract's areas (decision 169: from each area's lines file, else its typed km); null without areas. */
export function contractLineKm(areas: readonly ContractArea[] | null | undefined): number | null {
  if (!areas || areas.length === 0) return null;
  return areas.reduce((a, x) => a + areaLineKm(x), 0);
}

/** What a contract variation says about line km (`contract_variation`). */
export interface VariationKm {
  readonly number: number;
  /** Signed change of the contract's line km (+ or −); null when the variation did not change the km. */
  readonly line_km_change: number | null;
  readonly client_agreed: boolean;
}

export interface ContractKm {
  /** The signed contract's line km (`contractLineKm`). */
  readonly signedKm: number;
  /** Σ km change of the counted variations. */
  readonly variationKm: number;
  /** The contract's km now: signed km plus the counted variations. */
  readonly km: number;
  /** Numbers of the variations counted: the client agreed them and they record a km change. */
  readonly variations: readonly number[];
}

/**
 * The contract's line km after its variations (decision 258): the signed contract's km plus the km change of every
 * variation the client agreed. A variation with no km figure changes nothing, so the signed km stays (today's
 * behaviour). Null without a signed contract's areas.
 */
export function contractKm(
  areas: readonly ContractArea[] | null | undefined,
  variations: readonly VariationKm[] = [],
): ContractKm | null {
  const signedKm = contractLineKm(areas);
  if (signedKm === null) return null;
  const counted = variations
    .filter((v) => v.client_agreed && v.line_km_change !== null && Number.isFinite(v.line_km_change))
    .sort((a, b) => a.number - b.number);
  const variationKm = counted.reduce((a, v) => a + (v.line_km_change ?? 0), 0);
  return { signedKm, variationKm, km: signedKm + variationKm, variations: counted.map((v) => v.number) };
}

export interface OpsProgress {
  readonly plannedKm: number;
  readonly acceptedKm: number;
  readonly remainingKm: number;
  /** Accepted ÷ planned, whole per cent, truncated (the Job Summary's Est % Complete). */
  readonly percentComplete: number;
  /** The contract's km after the agreed variations with km (decision 258); null without a signed contract. */
  readonly contractKm: number | null;
  /** The signed contract's own km, before variations. */
  readonly contractSignedKm: number | null;
  /** Numbers of the variations whose km are in `contractKm`. */
  readonly contractVariations: readonly number[];
  /** Accepted ÷ contract km, whole per cent, truncated; null without a signed contract. */
  readonly percentOfContract: number | null;
  /** Live blocks ticked "Job km are completed" (N32): their lines count 0 km remaining. */
  readonly kmCompletedBlocks: number;
}

/**
 * A project's progress to date (the Progress page's total) and against its contract's line km after variations.
 * Per cent of plan (`percentComplete`) stays the headline; per cent of contract sits beside it. The project's
 * blocks carry the "Job km are completed" tick (MB, 2 Nov 2026: the Operations figures read it as Teslyn Field's
 * Project overview does); without them the remaining km are the plan's.
 */
export function opsProgress(
  statuses: readonly LineStatus[],
  plan: readonly PlannedLine[],
  contractAreas: readonly ContractArea[] | null,
  variations: readonly VariationKm[] = [],
  blocks: readonly Pick<Block, 'id' | 'name' | 'deleted_at' | 'km_completed'>[] = [],
): OpsProgress {
  const live = blocks.filter((b) => b.deleted_at === null);
  const total = projectProgress(statuses, plan, live).total;
  const c = contractKm(contractAreas, variations);
  return {
    ...total,
    kmCompletedBlocks: live.filter(blockKmCompleted).length,
    contractKm: c?.km ?? null,
    contractSignedKm: c?.signedKm ?? null,
    contractVariations: c?.variations ?? [],
    percentOfContract: c === null || c.km <= 0 ? null : estPercentComplete(total.acceptedKm, c.km),
  };
}
